'use client';

import React from 'react';
import { Divider } from 'ui';
import BaseBanner from '~/components/Chrome/Banners/BaseBanner';
import useKnownCookie from '~/hooks/useKnownCookie';
import ChevronRight from '~/public/static/svg/ChevronRight';
import CloseIcon from '~/public/static/svg/CloseIcon';
import TagIcon from '~/public/static/svg/TagIcon';
import { RailsSale } from '~/typings/services/rails/sales';

import { isSaleBuySide } from '~/api/appDir/saleUtils';
import LockerLink from '~/components/Links/LockerLink';
import LoginLink from '~/components/Links/LoginLink';
import ShopLink from '~/components/Links/ShopLink';

function GlobalStyle() {
  return (
    <style jsx global>
      {`
        #nprogress .bar {
          background: #4a5568 !important;
        }

        #nprogress .peg {
          box-shadow:
            0 0 10px #4a5568,
            0 0 5px #4a5568 !important;
        }
      `}
    </style>
  );
}

function SaleBanner({
  sale,
  mobile,
  username,
}: {
  mobile: boolean;
  sale: RailsSale;
  username?: string;
}) {
  const [cookieHidden, setCookieHidden] = useKnownCookie('dismissed_sale');

  if (cookieHidden) {
    return null;
  }

  function onClickClose(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setCookieHidden(String(sale.id));
  }

  const isBuySide = isSaleBuySide(sale);

  const banner = mobile ? (
    <MobileBanner isBuySide={isBuySide} onClose={onClickClose} sale={sale} />
  ) : (
    <DesktopBanner isBuySide={isBuySide} onClose={onClickClose} sale={sale} />
  );

  if (isBuySide) {
    return (
      <ShopLink path={sale.destination}>
        <GlobalStyle />
        {banner}
      </ShopLink>
    );
  } else if (username) {
    return (
      <LockerLink username={username}>
        <GlobalStyle />
        {banner}
      </LockerLink>
    );
  } else {
    return (
      <LoginLink message="Sign into your SidelineSwap account to add your items to the sale">
        <GlobalStyle />
        {banner}
      </LoginLink>
    );
  }
}

function MobileBanner({
  isBuySide,
  onClose,
  sale,
}: {
  isBuySide: boolean;
  onClose: React.MouseEventHandler;
  sale: RailsSale;
}) {
  const title = isBuySide
    ? `${sale.label} - ${sale.name}`
    : `${sale.name} starting soon`;

  return (
    <BaseBanner className="bg-primary-500 items-center text-white">
      <TagIcon />

      <div className="flex flex-col">
        <div className="flex items-center font-bold uppercase">
          <div className="mr-2">{title}</div>
          <ChevronRight fill="#fff" height={15} width={15} />
        </div>
        {/*<div className="leading-tight">{sale.subtitle}</div>*/}
      </div>

      <div className="w-full flex-1" />

      <Divider vertical />

      <button
        type="button"
        onClick={onClose}
        className="z-10 -m-2 p-4"
        title="Dismiss"
      >
        <CloseIcon fill="#fff" />
      </button>
    </BaseBanner>
  );
}

function DesktopBanner({
  isBuySide,
  onClose,
  sale,
}: {
  isBuySide: boolean;
  onClose: React.MouseEventHandler;
  sale: RailsSale;
}) {
  const title = isBuySide
    ? `${sale.label} - ${sale.name}`
    : `${sale.name} starting soon`;

  return (
    <BaseBanner className="bg-primary-500 items-center text-white">
      <TagIcon />

      <div className="font-bold uppercase">{title}</div>

      <Divider vertical />

      <div>{sale.subtitle}</div>

      <div className="w-full flex-1" />

      <div className="flex items-center">
        <div className="mr-2 font-bold uppercase">
          {isBuySide ? 'Shop Sale' : 'Add to Sale'}
        </div>

        <ChevronRight fill="#fff" height={15} width={15} />
      </div>

      <Divider vertical />

      <button
        type="button"
        onClick={onClose}
        className="z-10 -m-2 p-4"
        title="Dismiss"
      >
        <CloseIcon fill="#fff" />
      </button>
    </BaseBanner>
  );
}

export default SaleBanner;
